/**
 * Лента просит расширение поставить окна контор на половины стола.
 *
 * `window.open` с left/top в AdsPower SunBrowser не двигает окно: ядро режет
 * features как отпечаток. `chrome.windows.create` этого запрета нет.
 * Форма та же, что у `forq:open-tabs`: лента кладёт сообщение в своё окно.
 */

import type { SplitAxis } from '../next/deskLayout';

export const OPEN_PANES = 'forq:open-panes';
export const OPEN_PANES_OK = 'forq:open-panes-ok';
/** Реле приняло просьбу. Дальше окна его, страница ждёт итога, а не 600 мс. */
export const OPEN_PANES_WAIT = 'forq:open-panes-wait';

/** С этой версии служебный процесс умеет слоты, не только вкладки. */
export const PANES_MIN = '0.4.72';
/** С этой версии расширение отвечает, сколько окон контор село. */
export const PANES_ACK = '0.4.74';
/** С этой версии стол — три живых окна, страница не плодит window.open. */
export const WORKSPACE_MIN = '0.4.75';

export type PaneRole = 'book' | 'fork';

export type OpenPane = {
  url: string;
  left: number;
  top: number;
  width: number;
  height: number;
  role: PaneRole;
  source: string;
};

export type OpenPanesAsk = {
  channel: typeof OPEN_PANES;
  axis: SplitAxis;
  panes: OpenPane[];
  id?: string;
};

export type OpenPanesMode = 'panes' | 'tabs' | 'none';

export type OpenPanesOk = {
  channel: typeof OPEN_PANES_OK;
  id: string;
  ok: boolean;
  books: number;
  mode: OpenPanesMode;
};

const HTTP = /^https?:\/\//i;
const MAX = 4;

export function versionAtLeast(version: string | null, need: string): boolean {
  if (version === null || version === '') return false;
  const have = version.split('.').map((n) => Number.parseInt(n, 10) || 0);
  const want = need.split('.').map((n) => Number.parseInt(n, 10) || 0);
  const len = Math.max(have.length, want.length);
  for (let i = 0; i < len; i += 1) {
    const a = have[i] ?? 0;
    const b = want[i] ?? 0;
    if (a > b) return true;
    if (a < b) return false;
  }
  return true;
}

export function extPlacesPanes(version: string | null): boolean {
  return versionAtLeast(version, PANES_MIN);
}

export function extAcksPanes(version: string | null): boolean {
  return versionAtLeast(version, PANES_ACK);
}

export function extSeatsWorkspace(version: string | null): boolean {
  return versionAtLeast(version, WORKSPACE_MIN);
}

function finite(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n);
}

function validPane(raw: unknown): raw is OpenPane {
  if (typeof raw !== 'object' || raw === null) return false;
  const pane = raw as Partial<OpenPane>;
  if (typeof pane.url !== 'string' || !HTTP.test(pane.url) || pane.url.length >= 2048) return false;
  if (!finite(pane.left) || !finite(pane.top) || !finite(pane.width) || !finite(pane.height)) {
    return false;
  }
  if (pane.width < 100 || pane.height < 100 || pane.width > 10000 || pane.height > 10000) {
    return false;
  }
  if (Math.abs(pane.left) > 20000 || Math.abs(pane.top) > 20000) return false;
  if (pane.role !== 'book' && pane.role !== 'fork') return false;
  if (typeof pane.source !== 'string' || pane.source.length === 0 || pane.source.length > 200) {
    return false;
  }
  return true;
}

function askId(raw: unknown): string | undefined {
  return typeof raw === 'string' && raw.length > 0 && raw.length <= 64 ? raw : undefined;
}

export function openPanesEnvelope(
  axis: SplitAxis,
  panes: readonly OpenPane[],
  id?: string,
): OpenPanesAsk {
  return {
    channel: OPEN_PANES,
    axis: axis === 'stack' ? 'stack' : 'side',
    panes: panes.filter(validPane).slice(0, MAX),
    ...(askId(id) !== undefined ? { id: askId(id) } : {}),
  };
}

export function parseOpenPanes(data: unknown): OpenPanesAsk | null {
  if (typeof data !== 'object' || data === null) return null;
  const body = data as { channel?: unknown; axis?: unknown; panes?: unknown; id?: unknown };
  if (body.channel !== OPEN_PANES) return null;
  if (!Array.isArray(body.panes)) return null;
  const panes = body.panes.filter(validPane).slice(0, MAX);
  if (panes.length === 0) return null;
  const id = askId(body.id);
  return {
    channel: OPEN_PANES,
    axis: body.axis === 'stack' ? 'stack' : 'side',
    panes,
    ...(id !== undefined ? { id } : {}),
  };
}

export function openPanesOkEnvelope(
  id: string,
  result: { ok: boolean; books: number; mode?: unknown },
): OpenPanesOk {
  const mode: OpenPanesMode =
    result.mode === 'panes' || result.mode === 'tabs' || result.mode === 'none' ? result.mode : 'none';
  return {
    channel: OPEN_PANES_OK,
    id: askId(id) ?? '',
    ok: result.ok === true,
    books: Number.isFinite(result.books) ? Math.max(0, Math.floor(result.books)) : 0,
    mode,
  };
}

export function parseOpenPanesOk(data: unknown): OpenPanesOk | null {
  if (typeof data !== 'object' || data === null) return null;
  const body = data as { channel?: unknown; id?: unknown; ok?: unknown; books?: unknown; mode?: unknown };
  if (body.channel !== OPEN_PANES_OK) return null;
  const id = askId(body.id);
  if (id === undefined) return null;
  return openPanesOkEnvelope(id, {
    ok: body.ok === true,
    books: typeof body.books === 'number' ? body.books : 0,
    mode: body.mode,
  });
}

export type OpenPanesWait = { channel: typeof OPEN_PANES_WAIT; id: string };

export function openPanesWaitEnvelope(id: string): OpenPanesWait {
  return { channel: OPEN_PANES_WAIT, id: askId(id) ?? '' };
}

export function parseOpenPanesWait(data: unknown): OpenPanesWait | null {
  if (typeof data !== 'object' || data === null) return null;
  const body = data as { channel?: unknown; id?: unknown };
  if (body.channel !== OPEN_PANES_WAIT) return null;
  const id = askId(body.id);
  return id === undefined ? null : { channel: OPEN_PANES_WAIT, id };
}
